/** One field where the cluster and the source disagree. */
export interface DriftOperation {
  op: string;
  path: string;
  /** What the source declares. */
  value?: unknown;
  /** What the cluster currently has. */
  oldValue?: unknown;
  /** For an unmanaged field, the field manager that owns it. */
  manager?: string;
}

/**
 * Turns the identifier the engine reports into something readable.
 *
 * It arrives as namespace_name_group_kind, which is how the resource is keyed
 * everywhere else in the report, so it is decoded rather than replaced.
 */
export function describeResource(id: string | undefined): string {
  if (!id) {
    return 'Unknown resource';
  }

  const parts = id.split('_');
  if (parts.length < 4) {
    return id;
  }

  const [namespace, name, , kind] = parts;

  return namespace ? `${kind} ${name} in ${namespace}` : `${kind} ${name}`;
}

/**
 * Describes one difference as source then cluster, which is the direction the
 * header states and the direction a reconcile moves the resource in.
 *
 * Short on purpose: the path says where, the arrow says which way. A line that
 * spells out which side each value came from says the same thing three times and
 * stops being readable once a resource drifts in several places.
 *
 * A report from before the cluster's value was recorded names only what the
 * source declares, rather than claiming the field is unset.
 */
export function describeOperation(operation: DriftOperation): string {
  if (operation.op === 'missing') {
    return 'not on the cluster';
  }

  // A field another manager owns inside a structure the source declares. Named
  // with its owner, because whether a reconcile removes it depends on whose
  // hand it was: correction takes over a person's and leaves a controller's.
  if (operation.op === 'unmanaged') {
    const by = operation.manager ? ` by ${operation.manager}` : '';

    return operation.oldValue === undefined
      ? `${operation.path}: set${by} on the cluster, not in the source`
      : `${operation.path}: ${formatValue(operation.oldValue)} set${by} on the cluster, not in the source`;
  }

  if (operation.op === 'remove') {
    return operation.oldValue === undefined
      ? `${operation.path}: not in the source`
      : `${operation.path}: ${formatValue(operation.oldValue)} not in the source`;
  }

  if (operation.value === undefined) {
    return `${operation.path}: differs from the source`;
  }

  const declared = formatValue(operation.value);

  if (operation.oldValue !== undefined) {
    return `${operation.path}: ${declared} → ${formatValue(operation.oldValue)}`;
  }

  return operation.op === 'add'
    ? `${operation.path}: ${declared} → not set`
    : `${operation.path}: ${declared} declared by the source`;
}

/**
 * Reads the operations out of a patch, or returns null to show it raw.
 *
 * The engine stores one operation per line, each its own JSON object rather than
 * the array a JSON patch usually is.
 * A single unreadable line falls back for the whole patch, so what is shown is
 * always either fully decoded or exactly what was reported.
 */
export function parsePatch(
  patch: string | undefined
): Array<DriftOperation> | null {
  const lines = (patch ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return null;
  }

  const operations: Array<DriftOperation> = [];

  for (const line of lines) {
    const operation = parseOperation(line);
    if (!operation) {
      return null;
    }

    operations.push(operation);
  }

  return operations;
}

function formatValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function parseOperation(line: string): DriftOperation | null {
  try {
    const parsed: unknown = JSON.parse(line);

    return isDriftOperation(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isDriftOperation(value: unknown): value is DriftOperation {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as DriftOperation).op === 'string' &&
    typeof (value as DriftOperation).path === 'string'
  );
}
